import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { useWorkspace } from '@/features/workspaces/hooks';
import { EmailSendingDomain } from '@/models/emailSendingDomains';
import { useCompanyValues } from '@/providers';
import { usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useRef, useState } from 'react';
import { mapDomainInfoStep2Response } from './domainInfoFlowApi';
import { isValidDomainId } from './domainInfoFlowMapper';
import { useEmailSendingDomainConnector } from './useEmailSendingDomainConnector';

type UseEmailSendingDomainDetailOptions = {
  domainId: string | null;
  domainNameHint?: string;
  skip?: boolean;
};

export const useEmailSendingDomainDetail = ({ domainId, domainNameHint, skip = false }: UseEmailSendingDomainDetailOptions) => {
  const workspace = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { infoFlowId, connectorInstanceId, isInfoReady, isLoading: isConnectorLoading } = useEmailSendingDomainConnector();
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [domain, setDomain] = useState<EmailSendingDomain | null>(null);
  const [flowParams, setFlowParams] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const activeRequestId = useRef(0);

  const missingAudience = isAudienceSettled && !audienceId;

  const fetchDetail = useCallback(
    async (isRefetch = false) => {
      if (skip || !domainId || !isInfoReady || !infoFlowId || !connectorInstanceId || !audienceId) {
        return;
      }

      if (!isValidDomainId(domainId)) {
        setIsError(true);
        setDomain(null);
        setFlowParams({});
        setIsLoading(false);
        return;
      }

      const requestId = ++activeRequestId.current;

      try {
        if (!isRefetch) {
          setIsLoading(true);
        }
        setIsError(false);

        const response = await prepareStep({
          workspaceId: workspace.id,
          flowId: infoFlowId,
          audienceId,
          stepId: 'step2',
          stepData: { domain_id: domainId },
          connectorInstanceId,
        }).unwrap();

        if (requestId !== activeRequestId.current) {
          return;
        }

        const translatedResponse = translateI18nValues(response, currentLanguage);
        const result = mapDomainInfoStep2Response(translatedResponse, domainId, domainNameHint);

        setDomain(result.domain);
        setFlowParams(result.flowParams);
      } catch {
        if (requestId === activeRequestId.current) {
          setIsError(true);
          setDomain(null);
          setFlowParams({});
        }
      } finally {
        if (requestId === activeRequestId.current) {
          setIsLoading(false);
        }
      }
    },
    [audienceId, connectorInstanceId, currentLanguage, domainId, domainNameHint, infoFlowId, isInfoReady, prepareStep, skip, workspace.id]
  );

  const refetch = useCallback(() => fetchDetail(true), [fetchDetail]);

  useEffect(() => {
    if (skip || !domainId) {
      setDomain(null);
      setFlowParams({});
      setIsLoading(false);
      setIsError(false);
      return;
    }

    if (isConnectorLoading || isAudienceLoading) {
      return;
    }

    if (missingAudience) {
      setDomain(null);
      setFlowParams({});
      setIsLoading(false);
      setIsError(false);
      return;
    }

    if (isInfoReady) {
      void fetchDetail();
      return;
    }

    setIsLoading(false);
  }, [domainId, fetchDetail, isAudienceLoading, isConnectorLoading, isInfoReady, missingAudience, skip]);

  return {
    domain,
    flowParams,
    missingAudience,
    isLoading: skip || !domainId ? false : isConnectorLoading || isAudienceLoading || isLoading,
    isError,
    refetch,
    infoFlowId,
    connectorInstanceId,
  };
};
